import * as React from "react";
import { styled } from "../../ui/theme";
import Control from "../control/Control";
import Map from "../map/Map";
import Total from "./Total";
import Delta from "./Delta";
import Sterkte from "./Sterkte";
import { useStore } from "../../App";
import { observer } from "mobx-react";
import Chart from "../graph/Chart";
import Timeline from "../timeline/Timeline";
import Info from "../info/Info";
import { Spinner } from "@chakra-ui/core";
import { useWindowSize } from "@react-hook/window-size";
import { useSwipeable } from "react-swipeable";
import { useMotionValue, useSpring } from "framer-motion";

const pages = [0, 5, 11];

export default observer(function Grid() {
  const ref = React.useRef<HTMLDivElement>(null);

  const [page, _page] = React.useState(0);
  const [width, height] = useWindowSize();
  const store = useStore();
  const motion = useMotionValue(0);
  const spring = useSpring(motion, {
    stiffness: 300,
    damping: 100
  });
  const handlers = useSwipeable({
    onSwipedUp: e => {
      _page(p => {
        if (p + 1 < pages.length) {
          return p + 1;
        }
        return 0;
      });
    },
    onSwipedDown: e => {
      console.log("Down?");
      _page(p => {
        if (p - 1 >= 0) {
          return p - 1;
        }
        return pages.length - 1;
      });
    }
  });
  const targetHeight = width <= 800 ? height * 3 : height;

  React.useEffect(() => {
    spring.onChange(value => (ref.current.scrollTop = value));
  }, []);

  React.useEffect(() => {
    if (ref.current) {
      //ref.current.scrollTop = 200;
      let v = pages[page];
      v = Math.ceil((height / 6) * v);
      motion.set(v);
    }
  }, [page]);

  return (
    <Container ref={ref}>
      {!store.ready ? (
        <Loading>
          <Spinner color="green.400" size="xl" />
        </Loading>
      ) : (
        <Panels {...handlers} style={{ height: targetHeight + "px" }}>
          <PMap>
            <Map />
          </PMap>
          <PControl>
            <Control />
          </PControl>
          <PTotal>
            <Total />
          </PTotal>
          <PDelta>
            <Delta />
          </PDelta>
          <PSterkte>
            <Sterkte />
          </PSterkte>
          <PChart>
            <Chart />
          </PChart>
          <PTimeline>
            <Timeline />
          </PTimeline>
        </Panels>
      )}
      <Info />
    </Container>
  );
});

const Container = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  padding: 4px;
  background-color: ${props => props.theme.colors.gray["600"]};

  display: flex;
  align-items: stretch;
  overflow: hidden;
`;

const Panels = styled.div`
  flex: 1;
  display: grid;
  max-width: 100%;
  overflow: hidden;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr;
  grid-template-areas:
    "ma ma ma co co co"
    "ma ma ma to de st"
    "ma ma ma ch ch ch"
    "ma ma ma ch ch ch"
    "ma ma ma ch ch ch"
    "tl tl tl ch ch ch";

  grid-column-gap: 4px;
  grid-row-gap: 4px;
  @media (max-width: 800px) {
    grid-template-rows: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    grid-template-areas:
      "ma ma ma ma ma ma"
      "ma ma ma ma ma ma"
      "ma ma ma ma ma ma"
      "ma ma ma ma ma ma"
      "tl tl tl tl tl tl"
      "to to de de st st"
      "ch ch ch ch ch ch"
      "ch ch ch ch ch ch"
      "ch ch ch ch ch ch"
      "ch ch ch ch ch ch"
      "ch ch ch ch ch ch"
      "co co co co co co"
      "co co co co co co"
      "co co co co co co"
      "co co co co co co"
      "co co co co co co"
      "co co co co co co"
      "co co co co co co";
  }
`;

const Panel = styled.section`
  border-radius: 6px;
  background-color: #fff;
  display: flex;
  align-items: stretch;
  justify-content: space-around;
  overflow: hidden;
`;

const PMap = styled(Panel)`
  grid-area: ma;
`;

const PControl = styled(Panel)`
  grid-area: co;
  background-color: ${props => props.theme.colors.green["400"]};
`;

const PTotal = styled(Panel)`
  grid-area: to;
  background-color: ${props => props.theme.colors.gray["100"]};
`;

const PDelta = styled(Panel)`
  grid-area: de;
  background-color: ${props => props.theme.colors.gray["100"]};
`;

const PSterkte = styled(Panel)`
  grid-area: st;
  background-color: ${props => props.theme.colors.gray["100"]};
`;

const PChart = styled(Panel)`
  grid-area: ch;
`;

const PTimeline = styled(Panel)`
  grid-area: tl;
  background-color: ${props => props.theme.colors.gray["500"]};
`;

const Loading = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 1001;
  margin: -1.5rem 0 0 -1.5rem;
  width: 3rem;
  height: 3rem;
`;
